if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          scrollbarItems: this.querySelector('[id^="Scrollbar-"]'),
          dotsContainer: this.querySelector('[id^="DotsContainer"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.elements.dotsContainer
          .querySelectorAll('.gallery-dot')
          .forEach((dot) => {
            const mediaId = dot.dataset.target;
            dot.addEventListener('click', () => {
              this.setActiveMedia(mediaId, false);
            });
          });
        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce((event) => {
            this.onSlideChanged(event);
          }, 200)
        );

        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((mediaToSwitch, index) => {
              mediaToSwitch
                .querySelector('button')
                .addEventListener(
                  'click',
                  this.setActiveMedia.bind(
                    this,
                    mediaToSwitch.dataset.target,
                    false
                  )
                );
            });
        }

        if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) {
          this.removeListSemantic();
        }

        document.addEventListener('bulk-price:image-change', this.onBulkPriceImageChange.bind(this));
      }

      onSlideChanged(event) {
        const activeMediaId = event.detail.currentElement.dataset.mediaId;
        this.updateDots(activeMediaId);
      }

      setActiveMedia(mediaId, prepend) {
        // Try to find media within the slider list first (more reliable)
        const sliderList = this.elements.viewer.querySelector('ul[id^="Slider-Gallery"]');
        
        // Try multiple selector strategies to find the media element
        let activeMedia = null;
        if (sliderList) {
          // Try exact match first
          activeMedia = sliderList.querySelector(`li[data-media-id='${mediaId}']`);
          // If not found, try without quotes (in case of attribute parsing issues)
          if (!activeMedia) {
            activeMedia = sliderList.querySelector(`li[data-media-id="${mediaId}"]`);
          }
          // If still not found, try finding by ID (format: Slide-section-id-media-id)
          if (!activeMedia && mediaId.includes('-')) {
            const parts = mediaId.split('-');
            if (parts.length >= 2) {
              const slideId = `Slide-${mediaId}`;
              activeMedia = sliderList.querySelector(`#${slideId}`);
            }
          }
        }
        
        // Fallback: search in viewer directly
        if (!activeMedia) {
          activeMedia = this.elements.viewer.querySelector(`[data-media-id='${mediaId}']`);
          if (!activeMedia) {
            activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
          }
        }
        
        // If still not found, try searching in the entire media-gallery element
        if (!activeMedia) {
          activeMedia = this.querySelector(`[data-media-id='${mediaId}']`);
          if (!activeMedia) {
            activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`);
          }
        }
        
        if (!activeMedia) {
          console.warn(`MediaGallery: Could not find media element with id: ${mediaId}`);
          return;
        }
        
        // Get the currently active media (to hide it if it's a variant image)
        const currentlyActive = sliderList
          ? sliderList.querySelector('li.is-active')
          : this.elements.viewer.querySelector('li.is-active');
        
        // Remove active class from all media items
        const allMediaItems = sliderList
          ? sliderList.querySelectorAll('li[data-media-id]')
          : this.elements.viewer.querySelectorAll('[data-media-id]');
        allMediaItems.forEach((element) => {
          element.classList.remove('is-active');
        });
        
        // Hide all variant images (they should only show when they're the featured_media)
        const variantImages = sliderList
          ? sliderList.querySelectorAll('li[data-variant-image="true"]')
          : this.elements.viewer.querySelectorAll('[data-variant-image="true"]');
        variantImages.forEach((element) => {
          // Hide all variant images except the one being activated
          if (element !== activeMedia) {
            element.style.display = 'none';
          }
        });
        
        // If the currently active media was a variant image, hide it
        if (currentlyActive && currentlyActive.hasAttribute('data-variant-image') && currentlyActive !== activeMedia) {
          currentlyActive.style.display = 'none';
        }
        
        // Show and activate the selected media (this is the featured_media for the selected variant)
        activeMedia.style.display = '';
        activeMedia.classList.add('is-active');
        
        // If the media was hidden (variant-mobile-hide), make sure it's visible
        activeMedia.classList.remove('product__media-item--variant-mobile-hide');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target='${mediaId}']`
            );
            // Never prepend variant thumbnails - they should stay in their original position
            // Only prepend non-variant thumbnails
            if (activeThumbnail && !activeThumbnail.hasAttribute('data-variant-image')) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
            // For variant thumbnails, don't prepend - just ensure they're shown/hidden correctly
            // The setActiveThumbnail function will handle showing/hiding variant thumbnails
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        
        // Ensure the slider scrolls to show the active media
        window.setTimeout(() => {
          // If viewer has a slider component, scroll it to the active media
          if (this.elements.viewer.slider && sliderList) {
            // Scroll the slider to show the active media
            this.elements.viewer.slider.scrollTo({
              left: activeMedia.offsetLeft
            });
          } else if (this.elements.thumbnails && sliderList) {
            // Scroll the slider list directly if no slider component
            sliderList.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: 'smooth'
            });
          }
          
          // Also use scrollIntoView as a fallback for stacked layout
          if (
            !this.elements.thumbnails ||
            this.dataset.desktopLayout === 'stacked'
          ) {
            // Force a reflow to ensure the element is visible before scrolling
            activeMedia.offsetHeight;
            activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }
        }, 50);
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target='${mediaId}']`
        );
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition
        );
        this.updateDots(mediaId);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        const mediaId = thumbnail.dataset.target;
        const scrollbarItem = this.elements.scrollbarItems?.querySelector(
          `[data-target=${mediaId}]`
        );
        
        // Handle variant thumbnails: hide all except the active one, and position it correctly
        if (thumbnail.hasAttribute('data-variant-image')) {
          const variantThumbnails = this.elements.thumbnails.querySelectorAll('li[data-variant-image="true"]');
          const thumbnailList = this.elements.thumbnails.querySelector('ul[id^="Slider-Thumbnails"]');
          
          // Hide all variant thumbnails first
          variantThumbnails.forEach((element) => {
            if (element !== thumbnail) {
              element.style.display = 'none';
            }
          });
          
          // Show the active variant thumbnail
          thumbnail.style.display = '';
          
          // Move the active variant thumbnail to position 1 (right after featured_media thumbnail at position 0)
          // This ensures it appears in the correct position, not at the end
          if (thumbnailList && thumbnail.parentElement === thumbnailList) {
            // Find the featured_media thumbnail (usually the first one, id ends with "-0")
            const featuredThumbnail = thumbnailList.querySelector('li[id$="-0"]') || thumbnailList.querySelector('li:first-child');
            
            if (featuredThumbnail && featuredThumbnail !== thumbnail) {
              // Get the next sibling after featured_media (should be position 1)
              const nextSibling = featuredThumbnail.nextElementSibling;
              
              // Only move if thumbnail is not already in the correct position
              if (thumbnail !== nextSibling) {
                // If there's a next sibling and it's not a variant thumbnail, insert before it
                // Otherwise, insert after featured_media
                if (nextSibling && !nextSibling.hasAttribute('data-variant-image')) {
                  thumbnailList.insertBefore(thumbnail, nextSibling);
                } else {
                  featuredThumbnail.insertAdjacentElement('afterend', thumbnail);
                }
              }
            }
          }
        }
        
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        this.elements.scrollbarItems
          ?.querySelectorAll('span')
          .forEach((element) =>
            element.classList.remove('scrollbar__item--active')
          );
        scrollbarItem?.classList.add('scrollbar__item--active');
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      updateDots(activeMediaId) {
        const dots = this.elements.dotsContainer.querySelectorAll('span');
        let activeDot = null;
        
        dots.forEach((dot) => {
          dot.classList.remove('is-active');
          if (dot.dataset.target === activeMediaId) {
            dot.classList.add('is-active');
            activeDot = dot;
          }
        });
        
        // If the active dot is a variant dot, ensure it's positioned correctly (right after featured_media dot)
        if (activeDot && activeDot.hasAttribute('data-variant-image') && this.elements.dotsContainer) {
          const allDots = Array.from(this.elements.dotsContainer.querySelectorAll('span'));
          // The featured_media dot is always the first one (or the one that was initially active)
          // Find the first dot that's not a variant dot, or use the first dot
          const featuredDot = allDots.find(dot => !dot.hasAttribute('data-variant-image')) || allDots[0];
          
          // Only move if the active dot is not already in the correct position (right after featured_media)
          if (featuredDot && activeDot !== featuredDot) {
            const nextAfterFeatured = featuredDot.nextElementSibling;
            if (activeDot !== nextAfterFeatured) {
              // Move the active variant dot to position 1 (right after featured_media dot)
              featuredDot.insertAdjacentElement('afterend', activeDot);
            }
          }
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute('role', 'presentation')
        );
      }

      onBulkPriceImageChange(event) {
        const { imageSrc, imageSrcset, imageAlt } = event.detail;
        const sliderList = this.elements.viewer.querySelector('ul[id^="Slider-Gallery"]');
        if (!sliderList) return;
        
        // Extract base image URL for matching (without size parameters)
        const baseImageUrl = imageSrc ? imageSrc.split('?')[0].replace(/_[0-9]+x[0-9]+\./, '.') : null;
        const imageFilename = baseImageUrl ? baseImageUrl.split('/').pop().split('.')[0] : null;
        
        // Try to find the thumbnail that matches this image to get the media ID
        let targetMediaId = null;
        let matchingThumbnail = null;
        if (this.elements.thumbnails && imageSrc) {
          const allThumbnails = this.elements.thumbnails.querySelectorAll('li[data-target]');
          for (const thumb of allThumbnails) {
            const thumbImg = thumb.querySelector('img');
            if (thumbImg && thumbImg.src) {
              const thumbImageUrl = thumbImg.src.split('?')[0].replace(/_[0-9]+x[0-9]+\./, '.');
              const thumbImageFilename = thumbImageUrl.split('/').pop().split('.')[0];
              
              if (thumbImageFilename === imageFilename || thumbImg.src.includes(imageFilename) || thumbImageUrl === baseImageUrl) {
                targetMediaId = thumb.getAttribute('data-target');
                matchingThumbnail = thumb;
                break;
              }
            }
          }
        }
        
        // Try to find the variant media item that matches this image or media ID
        let targetMedia = null;
        const allMedia = sliderList.querySelectorAll(':scope > li[data-media-id]');
        
        // First, try to find by media ID if we found it from thumbnail
        if (targetMediaId) {
          targetMedia = sliderList.querySelector(`li[data-media-id="${targetMediaId}"]`);
        }
        
        // If not found by media ID, try to find by matching image source
        if (!targetMedia && imageSrc && baseImageUrl) {
          for (const media of allMedia) {
            const img = media.querySelector('img');
            if (img && img.src) {
              const mediaImageUrl = img.src.split('?')[0].replace(/_[0-9]+x[0-9]+\./, '.');
              const mediaImageFilename = mediaImageUrl.split('/').pop().split('.')[0];
              
              // Check if images match (by filename or URL)
              if (mediaImageFilename === imageFilename || mediaImageUrl === baseImageUrl || img.src.includes(imageFilename)) {
                targetMedia = media;
                targetMediaId = media.getAttribute('data-media-id');
                break;
              }
            }
          }
        }
        
        // Fallback to first visible or first media item (featured_media)
        if (!targetMedia) {
          targetMedia = sliderList.querySelector(':scope > li[data-media-id].is-active');
          if (!targetMedia) {
            for (const media of allMedia) {
              if (media.style.display !== 'none' && !media.classList.contains('product__media-item--variant-mobile-hide')) {
                targetMedia = media;
                break;
              }
            }
          }
          if (!targetMedia) {
            targetMedia = sliderList.querySelector(':scope > li[data-media-id]:first-child');
          }
        }
        
        if (!targetMedia) return;

        const targetImage = targetMedia.querySelector('img');

        if (targetImage && imageSrc) {
          // Store original values if not already stored
          if (!targetImage.dataset.originalSrc) {
            targetImage.dataset.originalSrc = targetImage.src;
            targetImage.dataset.originalSrcset = targetImage.srcset;
            targetImage.dataset.originalAlt = targetImage.alt;
          }

          // Update the image
          targetImage.src = imageSrc;
          if (imageSrcset) targetImage.srcset = imageSrcset;
          if (imageAlt) targetImage.alt = imageAlt;

          // Make sure the media item is visible (in case it was hidden)
          targetMedia.style.display = '';
          targetMedia.classList.remove('product__media-item--variant-mobile-hide');

          // Get the media ID if we don't have it yet
          if (!targetMediaId) {
            targetMediaId = targetMedia.dataset.mediaId;
          }
          
          // Update thumbnail if it exists
          const targetThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${targetMediaId}"] img`);
          if (targetThumbnail) {
            if (!targetThumbnail.dataset.originalSrc) {
              targetThumbnail.dataset.originalSrc = targetThumbnail.src;
              targetThumbnail.dataset.originalSrcset = targetThumbnail.srcset;
              targetThumbnail.dataset.originalAlt = targetThumbnail.alt;
            }
            targetThumbnail.src = imageSrc;
            if (imageSrcset) targetThumbnail.srcset = imageSrcset;
            if (imageAlt) targetThumbnail.alt = imageAlt;
            
            // Make sure thumbnail is visible
            const thumbnailItem = targetThumbnail.closest('li');
            if (thumbnailItem) {
              thumbnailItem.style.display = '';
              thumbnailItem.classList.remove('thumbnail-list__item--variant-mobile-hide');
            }
          } else if (matchingThumbnail) {
            // If we found a matching thumbnail but it's not linked to the media, update it
            const thumbImg = matchingThumbnail.querySelector('img');
            if (thumbImg) {
              if (!thumbImg.dataset.originalSrc) {
                thumbImg.dataset.originalSrc = thumbImg.src;
                thumbImg.dataset.originalSrcset = thumbImg.srcset;
                thumbImg.dataset.originalAlt = thumbImg.alt;
              }
              thumbImg.src = imageSrc;
              if (imageSrcset) thumbImg.srcset = imageSrcset;
              if (imageAlt) thumbImg.alt = imageAlt;
              matchingThumbnail.style.display = '';
              matchingThumbnail.classList.remove('thumbnail-list__item--variant-mobile-hide');
            }
          }

          // Activate the media - this will handle showing/hiding variant images correctly
          this.setActiveMedia(targetMediaId, false);
        }
      }

    }
  );
}
